import {useCallback, useEffect, useRef} from "react";

export const useUpdateEffect = (fn, dependency = []) => {
    const isInitialMount = useRef(true);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
        } else {
            return fn();
        }
    }, dependency)
};

export const useDebouncedEffect = (fn, dependency, delay = 500) => {
    const callback = useCallback(fn, dependency);

    useUpdateEffect(() => {
        const handler = setTimeout(() => {
            callback();
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [callback, delay]);
};

// This hook allows you to safely "useEffect" with async functions or promises
// and conditionally perform afterPromise only if the component is still
// mounted when the promise completes. This is useful for situations where
// you want to set the state of a component with the result from a promise
// and allows you to safely avoid the 'Can't perform a React state update on
// an unmounted component' error.
/**
 * @template T
 * @param {(function(): Promise<T>)|Promise<T>} promiseOrFunction
 * @param {function(T)} afterPromise
 * @param {*[]} deps
 */
export const useEffectPromise = (promiseOrFunction, afterPromise, deps) => {
    let stillMounted = true;

    useEffect(() => {
        const promise = (typeof promiseOrFunction === 'function')
            ? promiseOrFunction()
            : promiseOrFunction
        
        void (async () => {
            const result = await promise;
            if (stillMounted) {
                afterPromise(result);
            }
        })();
        return () => void (stillMounted = false);
    }, deps);
}
